import React, { useState, useRef } from 'react';
import {
    Divider,
    Box,
    Grid,
    FormControlLabel,
    Switch,
    CircularProgress,
} from '@mui/material';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SttButtonRefresh } from '../../../sporttia/all';
import { useOnViewport } from '../../../lib/hooks';
import { USER_MESSAGES } from '../../../lib/queryKeys';
import useMessagesService from '../../../services/MessagesService';
import MessageItem from './MessageItem';

type Message = {
    id: number;
    display?: string;
    sender?: {
        fullName: string;
    };
    img?: {
        id: number;
    };
    created: string;
    text: string;
    numUnread: number;
    comments?: {
        count: number;
    };
};

type MessagesPage = {
    rows: Message[];
    count: number;
    page: number;
    pages: number;
};

type Props = {
    fold: string;
};

/**
 * Infinite scroll with the messages of a user.
 * @param fold Fold type (INBOX|...).
 * @returns {React.ReactElement}
 */
export default function MessageList({ fold }: Props): React.ReactElement {
    const ROWS_PER_PAGE = 15;
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const ref = useRef<HTMLDivElement>(null);
    const messagesService = useMessagesService();
    const [onlyUnread, setOnlyUnread] = useState<boolean>(true);
    const { data, status, isFetchingNextPage, fetchNextPage, hasNextPage } =
        useInfiniteQuery<
            MessagesPage,
            Error,
            { pages: MessagesPage[]; pageParams: number[] },
            readonly unknown[],
            number
        >({
            queryKey: [
                USER_MESSAGES,
                {
                    fold,
                    isUnread: fold === 'INBOX' ? onlyUnread : false,
                },
            ],
            queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
                messagesService
                    .getList({
                        page: pageParam,
                        rows: ROWS_PER_PAGE,
                        fold,
                        isUnread: fold === 'INBOX' ? onlyUnread : false,
                    })
                    .then((response: unknown) => {
                        const typedResponse = response as MessagesPage;
                        typedResponse.page = pageParam;
                        return typedResponse;
                    }),
            getNextPageParam: (lastPage: MessagesPage) =>
                lastPage?.page < lastPage?.pages
                    ? lastPage?.page + 1
                    : undefined,
            initialPageParam: 1,
        });
    useOnViewport(ref as any, '-100px', fetchNextPage);

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid size={{ md: 4, sm: 5, xs: 6 }}>
                    <Box pl={1} pt={1}>
                        <b>{data?.pages[0]?.count}</b> {t('Messages')}
                    </Box>
                </Grid>
                <Grid size={{ md: 8, sm: 7, xs: 6 }}>
                    <Box sx={{ textAlign: 'right' }}>
                        {fold === 'INBOX' && (
                            <FormControlLabel
                                label={t('ShowOnlyNoRead')}
                                control={
                                    <Switch
                                        checked={onlyUnread}
                                        onChange={(
                                            ev: React.ChangeEvent<HTMLInputElement>,
                                        ) => setOnlyUnread(ev.target.checked)}
                                    />
                                }
                            />
                        )}

                        <Box ml={2} display="inline">
                            <SttButtonRefresh
                                onClick={() =>
                                    queryClient.invalidateQueries({
                                        queryKey: [USER_MESSAGES],
                                    })
                                }
                            />
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {status === 'pending' ? (
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            ) : status === 'error' ? (
                <>Error</>
            ) : (
                <>
                    {data?.pages.map((page: MessagesPage, i: number) => (
                        <React.Fragment key={i}>
                            {page.rows.map((msg: Message, msgKey: number) => (
                                <Box key={msgKey}>
                                    {msgKey > 0 && (
                                        <Box mt={1} mb={1}>
                                            <Divider />
                                        </Box>
                                    )}
                                    <MessageItem
                                        message={msg}
                                        fold={fold}
                                        onUpdate={() =>
                                            queryClient.invalidateQueries({
                                                queryKey: [USER_MESSAGES],
                                            })
                                        }
                                    />
                                </Box>
                            ))}
                        </React.Fragment>
                    ))}
                    {isFetchingNextPage && (
                        <Box display="flex" justifyContent="center">
                            <CircularProgress />
                        </Box>
                    )}
                    {!hasNextPage && (
                        <Box display="flex" justifyContent="center">
                            No hay más mensajes.
                        </Box>
                    )}
                </>
            )}

            {/* Este div es para detectar el final de la página, cuando sea visible se van a pedir datos a la API para realizar el scroll infinito. */}
            <div ref={ref as any} />
        </Box>
    );
}
